import { Box, Paper, Alert, Button, TextField, Autocomplete, MenuItem, Select, styled, Typography, Dialog, DialogActions, DialogContent, DialogTitle, Avatar } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Progress from "../../components/progress/Progress";
import macAddressUtils from "../../utils/macAddressUtils";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import IAdminPageBuildingDetail from "../../models/adminPage/adminPageBuildingDetail";
import IFloor from "../../models/floor";
import IProfile from "../../models/profile";
import ILocalEditRequest, { defaultLocalRequest } from "../../models/requests/local/localEditRequest";
import { defaultClosingSchedule, defaultOpeningSchedule } from "../../models/Schedule";
import { routes } from "../../routes";
import eslService from "../../services/esl/EslService";
import CleaningScheduleService from "../../services/local/CleaningScheduleService";
import localService from "../../services/local/LocalService";
import buildingService from "../../services/building/BuildingService";
import FloorService from "../../services/floor/FloorService";
import profileService from "../../services/profile/ProfileService";
import Topbar from "../../components/topbar/Topbar";
import Footer from "../../components/footer/Footer";
import InfoToolTip from "../../components/toolTip/InfoToolTip";
import { ReactComponent as LogoSvg } from "../../assets/images/svg/logo/hbscanLogo.svg";
import scanTag from "../../assets/images/scanTag.png";

export const EntryTitle = styled(Typography)(({ theme }) => ({
    fontSize: "0.7em",
    fontWeight: 600,
    display: "flex",
    flex: 0.3,
    marginLeft: theme.spacing(1),
    marginTop: "0.8em",
}));

export const FormWrapper = styled(Paper)(({ theme }) => ({
    display: "flex",
    flex: 2,
    flexDirection: "column",
    marginTop: "1em",
}));

export const EntryWrapper = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "95%"
}));

export const EntryField = styled(Box)(({ theme }) => ({
    display: "flex",
    flex: 1,
    flexDirection: "column",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: "2em",
    [theme.breakpoints.down("sm")]: {
        marginTop: "1.5em"
    }

}));


const InstallationPage = () => {
    const { buildingId, macAdress } = useParams();

    const { t } = useTranslation(["common", "admin"]);
    const [codeInput, setCodeInput] = useState("");

    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState("");

    const [localState, setLocalState] = useState<ILocalEditRequest>(defaultLocalRequest);

    const [buildings, setBuildings] = useState<IAdminPageBuildingDetail[]>([]);
    const [buildingFloors, setBuildingFloors] = useState<IFloor[]>([]);
    const [buildingProfiles, setBuildingProfiles] = useState<IProfile[]>([]);

    const [isFormSending, setIsFormSending] = useState(false);

    useEffect(() => {
        const onPopState = (event: PopStateEvent) => {
            event.preventDefault();

            if (location.pathname === routes.servicePlus.setupError) {
                navigate(routes.servicePlus.setupError, { replace: true });
            } else if (location.pathname === routes.servicePlus.setupComplete) {
                navigate(routes.servicePlus.setupComplete, { replace: true });
            }
        };

        window.addEventListener("popstate", onPopState);

        return () => {
            window.removeEventListener("popstate", onPopState);
        };
    }, [navigate, location.pathname]);


    useEffect(() => {
        if (!buildingId || !macAdress) return;

        const id = Number(buildingId || 0);
        setLocalState((old) => ({ ...old, buildingId: id, tagMacAddress: macAdress || "" }));

        const fetchData = async () => {
            const doesMacAddressExist = await eslService.GetDoesMacAddressExist(macAdress || "");
            if (doesMacAddressExist) {
                navigate(routes.servicePlus.setupError, { replace: true });
                return;
            }

            const building = await buildingService.GetBuildingById(id);
            setBuildings([building]);
            if (!building) {
                navigate(routes.login);
                return;
            }
            const floors = await FloorService.GetFloorsByBuildingId(id);
            setBuildingFloors(floors);

            const profiles = await profileService.GetProfilesByBuildingId(id);
            setBuildingProfiles(profiles);

        };

        fetchData();
    }, [buildingId, macAdress]);


    const updateLocalState = <T extends keyof ILocalEditRequest>(
        field: T,
        value: ILocalEditRequest[T]
    ) => {
        setLocalState((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const onCreateLocal = async (event: FormEvent) => {
        event.preventDefault();

        setErrorMessage("");
        setIsFormSending(true);

        if (localState.tagMacAddress) {
            const isEslMacValid = await eslService.GetIsValidMac(localState.tagMacAddress || "");
            if (!isEslMacValid) {
                setErrorMessage("table.local.macAddressErrorMessage");
                setIsFormSending(false);
                return;
            }
        }


        localService.PostLocal({ ...localState, openingSchedule: defaultOpeningSchedule, closingSchedule: defaultClosingSchedule })
            .then((response) => {

                if (response.status === 200) {
                    CleaningScheduleService.PostCleaningSchedule({ ...localState.localCleaning, localId: response.data });

                    navigate(routes.servicePlus.setupComplete, { replace: true });
                    return;
                }
                setErrorMessage("table.local.editErrorMessage");
            })
            .finally(() => setIsFormSending(false));
    };



    return <>
        <Topbar />
        <form autoComplete="off" onSubmit={onCreateLocal}>
            <FormWrapper sx={{ width: "100%", height: "85vh" }} >

                <>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            marginX: "auto",
                            padding: 4,
                            textAlign: "center",
                        }}
                    >
                        <Avatar
                            alt="hbscanLogo"
                            variant="square"
                            sx={{
                                background: "none",
                                width: 150,
                                height: 50,
                                marginBottom: 2,
                            }}
                        >
                            <LogoSvg />
                        </Avatar>
                        <Typography variant="h6">{t("servicePlus.title")}</Typography>
                    </Box>
                    {errorMessage && (
                        <Alert
                            severity="error"
                            sx={{ marginBottom: "1em" }}>
                            {t(errorMessage)}
                        </Alert>
                    )}
                    <EntryWrapper>
                        <EntryTitle>{t("table.local.building")}</EntryTitle>
                        <EntryField>
                            <Select
                                disabled
                                required={true}
                                value={localState.buildingId}
                                onChange={(e) =>
                                    updateLocalState(
                                        "buildingId",
                                        Number(e.target.value)
                                    )
                                }
                                sx={{ width: "100%", height: "2em" }}
                                MenuProps={{
                                    style: { overflowY: "visible" },
                                }}
                            >
                                {buildings && buildings.map(
                                    (building, index) =>
                                        <MenuItem
                                            key={index}
                                            value={building.id}
                                        >
                                            {building.name}
                                        </MenuItem>
                                )}
                            </Select>
                        </EntryField>
                    </EntryWrapper>


                    <EntryWrapper>
                        <EntryTitle>
                            {t("table.local.tagMacAddress")}
                        </EntryTitle>
                        <EntryField>
                            <TextField
                                disabled
                                required={false}
                                style={{ textAlign: "left", width: "100%" }}
                                value={localState.tagMacAddress}
                                error={
                                    (localState.tagMacAddress?.length ?? 0) > 0 &&
                                    !macAddressUtils.isMacAdrress(localState.tagMacAddress ?? "")
                                }
                                helperText={!macAddressUtils.MAC_ADDRESS_REGEX.test(localState.tagMacAddress ?? '') ? t("table.local.macAddressErrMsg") : ""}
                            />
                        </EntryField>
                    </EntryWrapper>

                    <EntryWrapper>
                        <EntryTitle>
                            {t("table.local.name")}
                        </EntryTitle>
                        <EntryField>
                            <TextField
                                required={true}
                                style={{ textAlign: "left", width: "100%" }}
                                value={localState.name}
                                onChange={(e) => {
                                    updateLocalState(
                                        "name",
                                        e.target.value
                                    );
                                }}
                            />
                        </EntryField>
                    </EntryWrapper>
                    <EntryWrapper>
                        <EntryTitle>{t("table.local.floor")}</EntryTitle>
                        <EntryField>
                            <Select
                                required={true}
                                value={localState.floorId}
                                onChange={(e) =>
                                    updateLocalState(
                                        "floorId",
                                        Number(e.target.value)
                                    )
                                }
                                sx={{ width: "100%", height: "2em" }}
                                MenuProps={{
                                    style: { overflowY: "visible" },
                                }}
                            >
                                {buildingFloors && buildingFloors.map(
                                    (floor, index) =>
                                        <MenuItem
                                            key={index}
                                            value={floor.id}
                                        >
                                            {floor.name}
                                        </MenuItem>
                                )}
                            </Select>
                        </EntryField>
                    </EntryWrapper>

                    <EntryWrapper>
                        <EntryTitle>{t("table.local.profile")}</EntryTitle>
                        <EntryField>
                            <Select
                                required={true}
                                value={localState.profileId}
                                onChange={(e) =>
                                    updateLocalState(
                                        "profileId",
                                        Number(e.target.value)
                                    )
                                }
                                sx={{ width: "100%", height: "2em" }}
                                MenuProps={{
                                    style: { overflowY: "visible" },
                                }}
                            >
                                {buildingProfiles && buildingProfiles.map(
                                    (profile, index) =>
                                        <MenuItem
                                            key={index}
                                            value={profile.id}
                                        >
                                            {profile.name}
                                        </MenuItem>
                                )}
                            </Select>
                        </EntryField>
                    </EntryWrapper>

                    <EntryWrapper >
                        <InfoToolTip
                            label="Codes"
                            labelVariant="body1"
                            labelStyle={(theme) => ({
                                fontSize: "0.7em",
                                fontWeight: 600,
                                display: "flex",
                                flex: 0.3,
                                marginLeft: theme.spacing(1),
                                marginTop: "0.8em",
                            })}
                            iconSize="medium"
                            sx={{ fontSize: '8px', padding: "1px", marginLeft: 1 }}
                        >
                            <div  >
                                <img src={scanTag} />
                            </div>
                        </InfoToolTip>
                        <EntryField>
                            <Autocomplete
                                fullWidth
                                multiple
                                freeSolo
                                value={localState.codes}
                                onChange={(_, value) => {
                                    updateLocalState(
                                        "codes",
                                        value
                                    );
                                }}
                                inputValue={codeInput}
                                onInputChange={(_, value) => {
                                    setCodeInput(value);
                                }}
                                options={[]}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        onBlur={(event) => {
                                            const value = event.target.value.trim();
                                            if (!value || localState.codes.includes(value)) return

                                            updateLocalState("codes", [...localState.codes, value]);
                                            setCodeInput("");
                                        }}
                                    />
                                )}
                            />
                        </EntryField>
                    </EntryWrapper>

                </>

                <Box sx={{ alignSelf: 'flex-end', marginTop: 'auto' }}>
                    <Progress isLoading={isFormSending}>

                        <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            disabled={
                                !localState.name.trim() || !localState.buildingId || !localState.floorId ||
                                !localState.profileId || localState.codes.length === 0 || !macAddressUtils.isMacAdrress(localState.tagMacAddress ?? "")
                            }
                            sx={{
                                marginBlock: "1em",
                            }}
                        >
                            {t("table.local.create")}
                        </Button>
                    </Progress>
                </Box>
            </FormWrapper>
        </form>
        {/* </Box > */}
        <Footer hasLanguageSelector={false} />
    </>
}



export default InstallationPage;

